import React, { useState } from "react";

import {
    ArrowForward as ArrowForwardIcon,
    ExpandLess as ExpandLessIcon,
    FilterList as FilterListIcon,
    InfoOutlined as InfoOutlinedIcon,
    Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Collapse,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

const EventMappingBox = ({
    eventMappings = [],
    currentTrackerId,
    type = "source", // 'source' or 'destination'
    onViewMappings,
    language = {},
}) => {
    const [expanded, setExpanded] = useState(false);
    const [showAll, setShowAll] = useState(false);

    if (!eventMappings || eventMappings.length === 0) {
        return null;
    }

    const getEventMappingChips = (events) => {
        const visibleEvents = showAll ? events : events.slice(0, 3);
        const remainingCount = events.length - 3;

        const getMappingTypeTooltip = (mappingType) => {
            if (mappingType === "clone") {
                return (
                    language?.event_mappings?.clone_tooltip ||
                    "Clone Mapping: Events are directly copied from source to destination without modification"
                );
            } else if (mappingType === "replace") {
                return (
                    language?.event_mappings?.replace_tooltip ||
                    "Replace Mapping: Events are replaced with new events from source to destination"
                );
            } else {
                return "";
            }
        };

        return (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {visibleEvents.map((event, index) => {
                    const mappingType = event.type || "filter";
                    const mappingLabel = `${
                        event.source_event?.code || "Unknown"
                    } → ${event.destination_event?.code || "Unknown"}`;

                    return (
                        <Tooltip
                            key={index}
                            title={getMappingTypeTooltip(mappingType)}
                            arrow
                            placement="top"
                        >
                            <Chip
                                label={mappingLabel}
                                size="small"
                                variant="outlined"
                                icon={
                                    mappingType === "clone" ? (
                                        <ArrowForwardIcon />
                                    ) : (
                                        <FilterListIcon />
                                    )
                                }
                                sx={{
                                    fontSize: "0.75rem",
                                    height: "24px",
                                    cursor: "help",
                                    "& .MuiChip-label": {
                                        px: 1,
                                    },
                                }}
                                onClick={() => {}}
                            />
                        </Tooltip>
                    );
                })}
                {!showAll && remainingCount > 0 && (
                    <Tooltip
                        title={
                            language?.event_mappings?.show_more_tooltip ||
                            "Click to view all event mappings"
                        }
                        arrow
                    >
                        <Chip
                            label={`+${remainingCount} more`}
                            size="small"
                            variant="outlined"
                            color="default"
                            onClick={() => setShowAll(true)}
                            sx={{
                                fontSize: "0.75rem",
                                height: "24px",
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        />
                    </Tooltip>
                )}
            </Stack>
        );
    };

    const getTrackerInfo = (mapping) => {
        if (type === "source") {
            return {
                name:
                    mapping.destination_tracker?.tracker_name ||
                    "Unknown Tracker",
                id: mapping.destination_tracker?.id,
            };
        } else {
            return {
                name: mapping.source_tracker?.tracker_name || "Unknown Tracker",
                id: mapping.source_tracker?.id,
            };
        }
    };

    const totalEvents = eventMappings.reduce(
        (sum, mapping) => sum + mapping.events.length,
        0,
    );

    return (
        <Card
            sx={{
                mb: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: "background.paper",
                zIndex: 9999,
                position: "relative",
                boxShadow: "none",
                mt: 6,
            }}
            style={{
                width: "100%",
            }}
        >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, fontSize: "1rem" }}
                        >
                            {language?.event_mappings?.title ||
                                "Event Mappings"}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Tooltip
                            title={
                                language?.event_mappings?.view_all ||
                                "View all mappings in detail"
                            }
                        >
                            <IconButton
                                size="small"
                                onClick={() =>
                                    onViewMappings && onViewMappings()
                                }
                                sx={{
                                    color: "warning.main",
                                    "&:hover": {
                                        backgroundColor: "warning.light",
                                        color: "white",
                                    },
                                }}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(!expanded)}
                            sx={{
                                transform: expanded
                                    ? "rotate(0deg)"
                                    : "rotate(180deg)",
                                transition: "transform 0.2s ease-in-out",
                            }}
                        >
                            {<ExpandLessIcon />}
                        </IconButton>
                    </Box>
                </Box>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5, fontSize: "0.875rem", lineHeight: 1.5 }}
                >
                    {language?.event_mappings?.description ||
                        "Event mappings define how events are transferred between trackers. "}
                    {totalEvents} {totalEvents === 1 ? "mapping" : "mappings"}{" "}
                    {language?.event_mappings?.across_text || "across"}{" "}
                    {eventMappings.length}{" "}
                    {eventMappings.length === 1 ? "tracker" : "trackers"}
                </Typography>

                <Collapse in={expanded}>
                    <Box
                        sx={{
                            maxHeight: "400px",
                            overflowY: "auto",
                            pr: 1,
                            mt: 1,
                        }}
                    >
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                backgroundColor: "action.hover",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontSize: "0.75rem",
                                    display: "block",
                                    mb: 0.5,
                                }}
                            >
                                {language?.event_mappings?.expanded_hint ||
                                    "💡 Tip: Hover over event chips to see mapping types"}
                            </Typography>
                        </Box>
                        <Stack spacing={2}>
                            {eventMappings.map((mapping, mappingIndex) => {
                                const trackerInfo = getTrackerInfo(mapping);

                                return (
                                    <Box key={mappingIndex}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: 1.5,
                                                mr: 1,
                                            }}
                                        >
                                            <Tooltip
                                                title={
                                                    type === "source"
                                                        ? language
                                                              ?.event_mappings
                                                              ?.to_tracker_tooltip ||
                                                          `Events are mapped to ${trackerInfo.name} tracker`
                                                        : language
                                                              ?.event_mappings
                                                              ?.from_tracker_tooltip ||
                                                          `Events are mapped from ${trackerInfo.name} tracker`
                                                }
                                                arrow
                                                placement="top"
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mr: 1,
                                                        maxWidth: "30rem",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        cursor: "help",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                    }}
                                                    title={trackerInfo.name}
                                                >
                                                    {type === "source"
                                                        ? language
                                                              ?.event_mappings
                                                              ?.to_label || "To"
                                                        : language
                                                              ?.event_mappings
                                                              ?.from_label ||
                                                          "From"}
                                                    : {trackerInfo.name}
                                                </Typography>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    language?.event_mappings
                                                        ?.events_count_tooltip ||
                                                    `Number of event mappings for this tracker`
                                                }
                                                arrow
                                            >
                                                <Chip
                                                    label={`${
                                                        mapping.events.length
                                                    } ${
                                                        mapping.events
                                                            .length === 1
                                                            ? language
                                                                  ?.event_mappings
                                                                  ?.event_singular ||
                                                              "event"
                                                            : language
                                                                  ?.event_mappings
                                                                  ?.event_plural ||
                                                              "events"
                                                    }`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ cursor: "help" }}
                                                    onClick={() => {}}
                                                />
                                            </Tooltip>
                                        </Box>

                                        {getEventMappingChips(mapping.events)}

                                        {mappingIndex <
                                            eventMappings.length - 1 && (
                                            <Divider sx={{ mt: 2 }} />
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                </Collapse>

                {!expanded && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1.5,
                            p: 1.5,
                            backgroundColor: "background.default",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.75rem" }}
                        >
                            {language?.event_mappings?.collapsed_summary ||
                                `📊 ${eventMappings.length} ${
                                    eventMappings.length === 1
                                        ? "tracker"
                                        : "trackers"
                                } connected with ${totalEvents} ${
                                    totalEvents === 1 ? "mapping" : "mappings"
                                }. Click to expand and view details.`}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default EventMappingBox;
